import {
  RACModal,
  RACButton,
  makeStyles,
  RACSelect,
  TextField,
} from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { orderByDescField } from '../../../context/PastDueListSearchCriteria/PastDueListSearchCriteriaProvider';
import { getReference } from '../../../api/reference';
import { ReferenceKeys } from '../../../types/types';
import { mapReferenceResponse, pipe } from '../../../utils/utils';
import {
  API_ERROR_MESSAGE,
  CACHED_KEYS,
  LOG_ACTIVITY_NOTES_MAX_LENGTH,
  SELECT_ONE_OPTION,
} from '../../../constants/constants';
import { Option } from '../../../types/types';
import i18n from 'i18next'; // Import i18next
import { useStoreDetails } from '../../../context/Store/StoreProvider';

export const addSelectOneOption = (arrayWithOptions: any[]) => {
  if (!arrayWithOptions) {
    return [
      {
        label: i18n.t(SELECT_ONE_OPTION),
        value: '',
      },
    ];
  }

  return [
    {
      label: i18n.t(SELECT_ONE_OPTION),
      value: '',
    },
    ...arrayWithOptions,
  ];
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (callResult: string, note: string) => void;
}
export const useStyles = makeStyles((theme: any) => ({
  dialogContent: {
    textAlign: 'left',
    height: theme.typography.pxToRem(400),
  },
  dialogRoot: {
    '& .MuiDialogContent-root': {
      padding: '1rem',
    },
    '& .MuiDialog-paperWidthSm': {
      maxWidth: theme.typography.pxToRem(500),
      maxHeight: theme.typography.pxToRem(363),
    },
    '& .MuiTypography-h4': {
      fontSize: '20px !important',
    },
    '& .MuiOutlinedInput-multiline': {
      padding: theme.typography.pxToRem(10),
    },
    '& .MuiInputBase-multiline': {
      margin: '8px 0px 0px 0px !important',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'OpenSans-regular',
      fontSize: theme.typography.pxToRem(14),
    },
  },
  notesLabel: {
    color: theme.palette.text.primary,
    transform: 'scale(1) !important',
    ...theme.typography.body1,
    position: 'relative',
    display: 'block',
  },
  dialogActions: {
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(15),
  },
  notesWrapper: {
    display: 'inline-flex',
    position: 'relative',
    flexDirection: 'column',
    width: '100%',
    marginTop: theme.typography.pxToRem(24),
    marginBotton: theme.typography.pxToRem(16),
  },
  callResultWrapper: {
    width: '75%',
  },
  callResultOptionsPaper: {
    maxHeight: theme.typography.pxToRem(150),
  },
  notesCharacterCount: {
    marginTop: theme.typography.pxToRem(5),
  },
}));

export interface LogActivityModalContentProps {
  callResultOptions: Option[];
  apiError: boolean;
  callResult: string;
  note: string;
  isLoading: boolean;

  setCallResult: (result: string) => void;
  setNote: (note: string) => void;
}
const LogActivityModalContent = ({
  callResultOptions,
  apiError,
  callResult,
  note,
  isLoading,

  setCallResult,
  setNote,
}: LogActivityModalContentProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { MXAccountMgmtAccess } = useStoreDetails();
  const translatedcallResultOptions = () => {
    const removeOptions = ['LM Answering Machine', 'Unknown HT Type'];
    // eslint-disable-next-line no-console
    console.log('MXAccountMgmtAccess:', MXAccountMgmtAccess);
    const filteredOptions = MXAccountMgmtAccess
      ? callResultOptions.filter(
          (option: any) => !removeOptions.includes(option.label)
        )
      : callResultOptions;

    return filteredOptions.map((option: any) => ({
      ...option,
      label: t(option.label),
    }));
  };

  return (
    <>
      <div className={classes.callResultWrapper}>
        <RACSelect
          classes={{ paper: classes.callResultOptionsPaper }}
          inputLabel={t('Call Result')}
          isDisabled={isLoading}
          defaultValue={callResult}
          loading={isLoading && !callResult.length}
          options={translatedcallResultOptions()}
          onChange={(e: React.ChangeEvent<{ value: any }>) =>
            setCallResult(e.target.value)
          }
          {...(apiError && {
            errorMessage: t(API_ERROR_MESSAGE),
          })}
        />
      </div>
      <div className={classes.notesWrapper}>
        <label className={classes.notesLabel}>{t('Notes')}</label>
        <TextField
          multiline
          disabled={isLoading}
          variant="outlined"
          value={note}
          key="logActivityNote"
          maxRows={3}
          minRows={3}
          onChange={(e) => {
            if (note.length >= LOG_ACTIVITY_NOTES_MAX_LENGTH) {
              const trimmedNote = e.target.value?.substring(
                0,
                LOG_ACTIVITY_NOTES_MAX_LENGTH
              );
              setNote(trimmedNote);
            } else {
              setNote(e.target.value);
            }
          }}
        />
        <span className={classes.notesCharacterCount}>
          ({LOG_ACTIVITY_NOTES_MAX_LENGTH}/{note.length})
        </span>
      </div>
    </>
  );
};

export const LogActivityModal = ({ open, onSave, onClose }: ModalProps) => {
  const classes = useStyles();
  const [callResultOptions, setCallResultOptions] = useState<Option[]>([]);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [callResult, setCallResult] = useState('');
  const [note, setNote] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    setIsLoading(true);
    getReference(
      [ReferenceKeys.CALL_RESULT],
      CACHED_KEYS.LOG_ACTIVITY_CACHED_KEY
    )
      .then((response: any) =>
        pipe(
          orderByDescField,
          mapReferenceResponse,
          addSelectOneOption,
          setCallResultOptions
        )(response?.references[0]?.referenceDetails)
      )
      .catch(() => {
        setApiError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleOnSave = () => {
    setIsLoading(true);
    onSave(callResult, note);
  };
  return (
    <RACModal
      isOpen={open}
      titleVariant="h4"
      classes={{
        dialogContent: classes.dialogContent,
        dialog: classes.dialogRoot,
        dialogActions: classes.dialogActions,
      }}
      maxWidth="sm"
      title={t('Log Activity')}
      content={
        <LogActivityModalContent
          callResultOptions={callResultOptions}
          apiError={apiError}
          isLoading={isLoading}
          callResult={callResult}
          note={note}
          setCallResult={setCallResult}
          setNote={setNote}
        />
      }
      onClose={() => {
        setCallResultOptions([]);
        setApiError(false);
        setCallResult('');
        setNote('');
        onClose();
      }}
      buttons={
        <>
          <RACButton variant="outlined" color="secondary" onClick={onClose}>
            {t('Cancel')}
          </RACButton>
          <RACButton
            disabled={!callResult.length || isLoading}
            variant="contained"
            color="primary"
            loading={isLoading && callResult.length > 0}
            onClick={handleOnSave}
          >
            {t('Save')}
          </RACButton>
        </>
      }
    />
  );
};
